import { AlertTriangle, ArrowLeft, LogOut, SquareCheck, SquarePlus, X } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'

import {
  MAX_TABS,
  WindowHost,
  useWindowEntry,
  useWindowShell,
  type MenuItem,
  type WindowFrameProps,
  type WindowNavigationState,
  type WindowView,
} from '@krp/client-runtime'
import { ActionInfo, Button, ModalFrame, panelId, tabId } from '@krp/ui-kit'

import { useMenuCounters, type MenuCounter } from '../bindings/menuCounters'
import { useMyAccess } from '../bindings/platform/useMyAccess'
import { signOutRequested } from '../composition/session'
import { HintsToggle } from './ActionHints'
import { useAppDispatch, useAppSelector } from './hooks'
import { MenuIcon } from './MenuIcon'
import { SectionSourceBar } from './SectionSourceBar'
import { SectionTabStrip } from './SectionTabStrip'
import { SECTION_TABS_PREFIX, hasTabStrip, isSectionTabWindow, useSectionTabs, type SectionTab, type SectionTabs } from './sectionTabs'
import { SessionGate } from './SessionGate'
import { selectMe } from './sessionSelectors'
import { ShellHealth } from './ShellHealth'
import { SourcedModalFrame } from './SourcedModalFrame'
import { ThemeSwitch } from './ThemeSwitch'
import { usePresence } from './usePresence'
import { BUILD_ID } from '../update/version'

/** Версия сборки в шапке, как в старом клиенте: короткий коммит, который подставляет сборка (vite.config.ts). */
const BUILD: string | undefined = import.meta.env.VITE_KRP_BUILD

const GROUPS = [
  { id: 'work', title: 'Работа' },
  { id: 'settings', title: 'Настройки' },
] as const

const MODAL_CLOSE_ACTION = 'shell.modal-close'

function Frame({ source, resourceId, ...frame }: WindowFrameProps) {
  if (source === null) return <ModalFrame {...frame} closeActionId={MODAL_CLOSE_ACTION} />
  return <SourcedModalFrame {...frame} source={source} resourceId={resourceId} closeActionId={MODAL_CLOSE_ACTION} />
}

/**
 * Каркас нового клиента: меню и заголовок — из реестра окон, вкладки — только
 * по кнопке «Вкладка» (карточка 274), возврат — ядро окон. Меню — РАЗДЕЛЫ, а
 * не источники (UI-SECTIONS, решение владельца 09.10.2026): ресурсы — вкладки
 * внутри раздела, и их столько, сколько ресурсов доступно человеку.
 */
export function Shell() {
  return (
    <SessionGate>
      {/* Доска сигналов связи окна (RP-UI-LINK) стоит в корне приложения (`ShellHealthRoot`): экраны и окна поверх сообщают свою связь, точка «Связь» в шапке одна. */}
      <ShellLayout />
    </SessionGate>
  )
}

function ShellLayout() {
  useWindowEntry()
  usePresence()
  const shell = useWindowShell()
  const commands = shell.useCommands()
  const tabs = shell.useTabs()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const me = useAppSelector(selectMe).data
  // Пункт с правом (`menu.requires`) виден только тому, кому сервер его выдал; пока права не пришли — не виден.
  const myApi = useMyAccess().data?.api
  const counters = useMenuCounters()
  const granted = (item: MenuItem) => item.requires === null || (myApi?.includes(item.requires) ?? false)
  const current = shell.match(pathname)
  const returnTo = shell.useReturn(current?.identity)
  const currentTab = current === undefined ? undefined : tabs.find((tab) => tab.id === current.identity)
  const location = `${pathname}${search}`

  // Раздел окна на экране: его вход или вкладка — полоса вкладок и имя раздела в шапке; карточка внутри раздела — только подсветка меню.
  const section = current?.section == null ? undefined : shell.section(current.section)
  const inSectionTabs = section !== undefined && current !== undefined && isSectionTabWindow(section, current)
  const sectionTabs = useSectionTabs(inSectionTabs ? section : undefined)
  // Раздел с плитками: под полосой «Источник» остаются только вкладки выбранного источника, а не всех сразу.
  const pickSource = inSectionTabs && section?.pickResource === true && current?.resourceId != null
  const strip = inSectionTabs && current !== undefined ? stripTabs(sectionTabs, current, pathname, pickSource) : null
  const showStrip =
    section !== undefined && hasTabStrip(section.id) && strip !== null && strip.activeId !== null && strip.tabs.length > 1
  // Вкладка и вход раздела называются именем раздела: источник виден на вкладке, а не в шапке.
  const title =
    section !== undefined && current !== undefined && (inSectionTabs || current.kind === section.homeKind)
      ? section.label
      : (current?.title ?? (pathname === '/' ? 'Главная' : 'Kalmykov Retail Platform'))

  const goBack = () => {
    const state: WindowNavigationState = { windowBack: true, windowTabSaved: true }
    void navigate(commands.close(location), { replace: true, state })
  }

  const switchTo = (id: string) => {
    const target = commands.switchToTab(id, location)
    if (target === null) return
    const state: WindowNavigationState = { windowTabSaved: true }
    void navigate(target, { state })
  }

  return (
    <div className="shell" data-owner-tabs={tabs.length > 0 || undefined}>
      {/* Меню слева во всю высоту, как в старом клиенте: бренд, под ним выбор темы, разделы, вкладки человека. */}
      <aside className="shell-side">
        <div className="shell-side__head">
          <div className="shell-side__brand">
            <ActionInfo id="shell.home" layout="block">
              <Link to="/" className="shell-brand" aria-label="Kalmykov Retail — главная">
                <span className="shell-brand__mark" aria-hidden="true">
                  K
                </span>
                <span className="shell-brand__name">Kalmykov Retail</span>
              </Link>
            </ActionInfo>
          </div>
          <ThemeSwitch />
        </div>
        <nav className="shell-menu" aria-label="Разделы">
          {GROUPS.map((group) => (
            <MenuGroup key={group.id} title={group.title} items={shell.menu(group.id).filter(granted)} current={current} pathname={pathname} counters={counters} />
          ))}
        </nav>
        {/* Вкладки — сразу под разделами (решения владельца 23.09 и 02.10.2026); новая встаёт последней. */}
        {tabs.length > 0 && (
          <nav className="shell-tabs" aria-label="Вкладки">
            <ActionInfo id="shell.tabs" layout="sibling">
              {tabs.map((tab) => {
                const active = tab.id === current?.identity
                return (
                  <span key={tab.id} className="shell-tabs__tab" data-active={active || undefined}>
                    <button type="button" onClick={() => switchTo(tab.id)} aria-current={active ? 'page' : undefined}>
                      {tab.title}
                    </button>
                    <button type="button" aria-label={`Закрыть вкладку ${tab.title}`} onClick={() => commands.closeTab(tab.id)}>
                      <X size={14} aria-hidden="true" />
                    </button>
                  </span>
                )
              })}
            </ActionInfo>
          </nav>
        )}
      </aside>

      <div className="shell-column">
        <header className="shell-header">
          {/* «Назад» — у окон-«детей» (карточка, мастер, форма) и у рабочего окна, открытого действием; между равными
              разделами и вкладками раздела (РМК ↔ Чеки) его нет: переключение — не вход вглубь. */}
          {current !== undefined && (!current.working || returnTo !== undefined) ? (
            <Button variant="ghost" data-size="sm" icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={goBack} actionId="shell.back">
              <span className="shell-header__label">Назад</span>
            </Button>
          ) : null}
          <h1 className="shell-header__title">{title}</h1>
          <div className="shell-header__tools">
            {current !== undefined &&
              (currentTab !== undefined ? (
                <Button
                  variant="secondary"
                  data-size="sm"
                  aria-pressed
                  icon={<SquareCheck size={14} aria-hidden="true" />}
                  title="Окно во вкладке. Нажмите, чтобы убрать вкладку"
                  onClick={() => commands.closeTab(currentTab.id)}
                  actionId="shell.tab"
                >
                  <span className="shell-header__label">Вкладка</span>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  data-size="sm"
                  aria-pressed={false}
                  disabled={tabs.length >= MAX_TABS}
                  icon={<SquarePlus size={14} aria-hidden="true" />}
                  title={tabs.length >= MAX_TABS ? `Вкладок уже ${MAX_TABS} — закройте ненужную` : 'Создать вкладку'}
                  onClick={() => commands.createTab(location)}
                  actionId="shell.tab"
                  disabledReason={tabs.length >= MAX_TABS ? `Вкладок уже ${MAX_TABS} — закройте ненужную` : null}
                >
                  <span className="shell-header__label">Вкладка</span>
                </Button>
              ))}
            <HintsToggle />
            <ShellHealth />
            {me?.displayName !== undefined && (
              <span className="shell-header__user" title={me.displayName}>
                {me.displayName}
              </span>
            )}
            <Button
              variant="ghost"
              data-size="sm"
              aria-label="Выйти"
              title="Выйти"
              icon={<LogOut size={14} aria-hidden="true" />}
              onClick={() => dispatch(signOutRequested())}
              actionId="shell.sign-out"
            />
            {BUILD !== undefined && (
              <span className="shell-header__version" title={BUILD_ID === undefined ? 'Версия сборки' : `Версия сборки: ${BUILD_ID}`}>
                {BUILD}
              </span>
            )}
          </div>
        </header>

        <main className="shell-main">
          {pickSource && section !== undefined && current !== undefined && (
            <SectionSourceBar
              source={sectionTabs.resources.find((item) => item.resource.id === current.resourceId)}
              onChange={() => {
                const state: WindowNavigationState = { windowFrom: returnTo ?? null }
                void navigate(section.path, { state })
              }}
            />
          )}
          {showStrip && strip.activeId !== null && (
            <SectionTabStrip label={section.label} icon={section.icon} tabs={strip.tabs} activeId={strip.activeId} returnTo={returnTo} />
          )}
          {/* Обёртка окна одна и та же с полосой и без: появление полосы (пришёл список источников) не пересоздаёт окно и
              не теряет его работу и запросы. */}
          <div
            className="shell-section-panel"
            {...(showStrip && strip.activeId !== null
              ? {
                  role: 'tabpanel',
                  id: panelId(SECTION_TABS_PREFIX, strip.activeId),
                  'aria-labelledby': tabId(SECTION_TABS_PREFIX, strip.activeId),
                }
              : {})}
          >
            <Outlet />
          </div>
        </main>
      </div>
      <WindowHost Frame={Frame} />
    </div>
  )
}

/**
 * Вкладки полосы и активная из них. Окно ресурса, которого нет среди
 * доступных (право сняли, ресурс выключен, адрес из закладки), всё равно
 * получает свою вкладку: открыто то, что в адресе, а не то, что в списке.
 */
function stripTabs({ query, tabs: all }: SectionTabs, current: WindowView, pathname: string, ownSourceOnly: boolean) {
  // Источник уже назван в полосе «Источник» выше: вкладка называет только занятие, без метки вида и имени ресурса.
  const tabs = ownSourceOnly
    ? all.filter((tab) => !tab.locked && tab.resourceId === current.resourceId).map((tab) => ({ ...tab, subtitle: null, badge: null, tone: null }))
    : all
  const activeIndex = tabs.findIndex((tab) => tab.id === current.identity)
  if (activeIndex >= 0) return { tabs, activeId: current.identity }
  // Пока список ресурсов не пришёл, «лишней» вкладки нет: окно, скорее всего, в нём окажется.
  if (tabs.length === 0 || query.status === 'loading' || query.status === 'idle') return { tabs, activeId: null }
  const extra: SectionTab = {
    id: current.identity,
    kind: current.kind,
    path: pathname,
    title: current.title,
    subtitle: null,
    badge: null,
    tone: null,
    resourceId: current.resourceId,
    locked: false,
  }
  return { tabs: [...tabs, extra], activeId: current.identity }
}

function MenuGroup({
  title,
  items,
  current,
  pathname,
  counters,
}: {
  title: string
  items: readonly MenuItem[]
  current: WindowView | undefined
  pathname: string
  counters: Readonly<Record<string, readonly MenuCounter[]>>
}) {
  const headingId = useId()
  const activeLink = useRef<HTMLAnchorElement | null>(null)
  // Страница внутри пункта (`presentation: 'page'`, «Тестирование» → «Поиск по фото») подсвечивает пункт, под адресом которого лежит.
  const activeKind = items.find(
    (item) =>
      current !== undefined &&
      (item.kind === current.kind ||
        (item.section !== null && item.section === current.section) ||
        (current.onAddress && !current.working && pathname.startsWith(`${item.path}/`))),
  )?.kind
  // Телефон: меню — полоса вбок; пункт открытого раздела не должен оставаться за краем.
  useEffect(() => {
    const link = activeLink.current
    if (link !== null && typeof link.scrollIntoView === 'function') link.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [activeKind])
  if (items.length === 0) return null
  return (
    <div className="shell-menu__group">
      <h2 className="shell-menu__heading" id={headingId}>
        {title}
      </h2>
      <ul aria-labelledby={headingId}>
        {items.map((item) => {
          const active = item.kind === activeKind
          return (
            <li key={item.kind}>
              <ActionInfo id="shell.menu" label={item.label} layout="block">
                <Link
                  ref={active ? activeLink : undefined}
                  to={item.path}
                  className="shell-menu__link"
                  aria-current={active ? 'page' : undefined}
                  data-active={active || undefined}
                >
                  <MenuIcon name={item.icon} size={16} />
                  <span>{item.label}</span>
                  {[...(item.section === null ? [] : (counters[item.section] ?? [])), ...(counters[item.kind] ?? [])].map((counter) => (
                    <span key={counter.label} className="shell-menu__count" role="status" aria-label={`${counter.label}: ${counter.count}`} title={counter.label}>
                      <AlertTriangle size={12} aria-hidden="true" />
                      {counter.count}
                    </span>
                  ))}
                </Link>
              </ActionInfo>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
